/**
 * Layout.tsx
 * 
 * A comprehensive layout system for the Stormfield Pre-qualification UI.
 * This component library encapsulates common layout patterns used throughout
 * the application to ensure consistency and maintainability.
 * 
 * Usage:
 * import { Card, Panel, TwoColumnLayout, FormSection } from '../ui/Layout';
 * 
 * <TwoColumnLayout>
 *   <Panel>Left content</Panel>
 *   <Panel>Right content</Panel>
 * </TwoColumnLayout>
 */

import React, { ReactNode } from 'react';

interface LayoutProps {
  children: ReactNode;
  className?: string;
}

interface CardProps extends LayoutProps {
  title?: ReactNode;
  footer?: ReactNode;
}

export interface PanelProps extends LayoutProps {
  width?: 'full' | '1/4' | '1/3' | '1/2' | '2/3' | '3/4';
  maxWidth?: string;
}

interface FormSectionProps extends LayoutProps {
  title?: ReactNode;
  description?: string;
}

interface GridProps extends LayoutProps {
  columns?: 1 | 2 | 3 | 4;
  gap?: 2 | 4 | 6 | 8;
}

/**
 * Card - A container with shadow, rounded corners, and padding
 * Style: bg-white rounded-lg shadow p-6
 */
export const Card: React.FC<CardProps> = ({ 
  children, 
  className = '',
  title,
  footer
}) => (
  <div className={`bg-white rounded-lg shadow p-6 ${className}`}>
    {title && <div className="mb-4">{title}</div>}
    <div>{children}</div>
    {footer && <div className="mt-4 pt-4 border-t border-gray-200">{footer}</div>}
  </div>
);

/**
 * Panel - A flexible width container for content
 * Style: w-full lg:w-[width] bg-white rounded-lg shadow p-6
 */
export const Panel: React.FC<PanelProps> = ({ 
  children, 
  className = '',
  width = 'full',
  maxWidth
}) => {
  const widthClasses = {
    'full': 'w-full',
    '1/4': 'w-full lg:w-1/4',
    '1/3': 'w-full lg:w-1/3',
    '1/2': 'w-full lg:w-1/2',
    '2/3': 'w-full lg:w-2/3',
    '3/4': 'w-full lg:w-3/4'
  };

  const maxWidthStyle = maxWidth ? { maxWidth } : {};

  return (
    <div 
      className={`${widthClasses[width]} bg-white rounded-lg shadow p-6 ${className}`}
      style={maxWidthStyle}
    >
      {children}
    </div>
  );
};

/**
 * FormSection - A section for grouping related form fields
 * Style: mb-4
 */
export const FormSection: React.FC<FormSectionProps> = ({ 
  children, 
  className = '',
  title,
  description
}) => (
  <div className={`mb-4 ${className}`}>
    {title && <div className="mb-1">{title}</div>}
    {description && <p className="text-sm text-gray-500 mb-2">{description}</p>}
    <div className="text-gray-900 text-sm space-y-1">
      {children}
    </div>
  </div>
);

/**
 * TwoColumnLayout - A responsive two-column layout
 * Style: flex flex-col lg:flex-row gap-8
 */
export const TwoColumnLayout: React.FC<LayoutProps> = ({ 
  children, 
  className = '' 
}) => (
  <div className={`flex flex-col lg:flex-row gap-8 ${className}`}>
    {children}
  </div>
);

/**
 * ThreeColumnLayout - A responsive three-column layout
 * Style: flex flex-col lg:flex-row gap-8
 */
export const ThreeColumnLayout: React.FC<LayoutProps> = ({ 
  children, 
  className = '' 
}) => (
  <div className={`flex flex-col lg:flex-row gap-8 ${className}`}>
    {children}
  </div>
);

/**
 * Grid - A responsive grid layout
 * Style: grid grid-cols-1 md:grid-cols-[columns] gap-[gap]
 */
export const Grid: React.FC<GridProps> = ({ 
  children, 
  className = '',
  columns = 2,
  gap = 4
}) => {
  const colClasses = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
  };

  return (
    <div className={`grid ${colClasses[columns]} gap-${gap} ${className}`}>
      {children}
    </div>
  );
};

/**
 * Container - A centered container with max width
 * Style: mx-auto max-w-7xl px-4
 */
export const Container: React.FC<LayoutProps> = ({ 
  children, 
  className = '' 
}) => (
  <div className={`mx-auto max-w-7xl px-4 ${className}`}>
    {children}
  </div>
);

/**
 * ActionBar - A container for action buttons
 * Style: flex flex-col sm:flex-row sm:flex-wrap gap-3 mb-6
 */
export const ActionBar: React.FC<LayoutProps> = ({ 
  children, 
  className = '' 
}) => (
  <div className={`flex flex-col sm:flex-row sm:flex-wrap gap-3 mb-6 ${className}`}>
    {children}
  </div>
);

/**
 * Divider - A horizontal divider
 * Style: border-t border-gray-200 my-4
 */
export const Divider: React.FC<{ className?: string }> = ({ 
  className = '' 
}) => (
  <hr className={`border-t border-gray-200 my-4 ${className}`} />
);

/**
 * ScrollableContainer - A container with horizontal scrolling
 * Style: overflow-x-auto
 */
export const ScrollableContainer: React.FC<LayoutProps> = ({ 
  children, 
  className = '' 
}) => (
  <div className={`overflow-x-auto ${className}`}>
    {children}
  </div>
);

/**
 * FixedOverlay - A fixed overlay for modals and loading states
 */
export const FixedOverlay: React.FC<LayoutProps> = ({ 
  children, 
  className = '' 
}) => (
  <div className={`fixed inset-0 bg-black/30 flex items-center justify-center z-50 pointer-events-auto ${className}`}>
    {children}
  </div>
);

/**
 * ModalContent - Content container for modals
 */
export const ModalContent: React.FC<LayoutProps> = ({ 
  children, 
  className = '' 
}) => (
  <div className={`bg-white min-w-[30rem] min-h-[10rem] rounded-lg p-8 shadow-xl flex flex-col items-center space-y-4 ${className}`}>
    {children}
  </div>
);
